import React, { useEffect, useState } from "react";
import { getAwaitingPayments, createSubscriptionOrder, verifyPayment, paymentWindowClosed } from "../apis/paymentsApi";
import { openRazorpayCheckout, rupees } from "../Utils/razorpayCheckout";
import { useAuth } from "../Context/AuthContext";
import { useNotification } from "../Context/NotificationContext";

// A paid plan chosen with the FPO / Company registration: the registration is active, and the plan starts once
// the user pays for it online (fpo PaymentService.awaitingPayment). Shown at the top of every page (Home.jsx) until
// it is paid; paying uses the same verified Razorpay flow as the subscription page.

export default function PlanAwaitingPaymentBanner() {
  const { reloadUserData } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => getAwaitingPayments().then((res) => setItems(res.data?.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const pay = async (item) => {
    setBusy(item.requestId);
    try {
      const order = (await createSubscriptionOrder(item.planId, item.serviceKey)).data?.data;
      const result = await openRazorpayCheckout(order);
      const tx = result.status === "paid"
        ? (await verifyPayment(result.orderId, result.paymentId, result.signature)).data?.data
        : (await paymentWindowClosed(order.orderId)).data?.data;
      if (tx?.status === "PAID") {
        showSuccess(`Payment received (receipt ${tx.receiptNo}). Your ${item.planName} plan is ${tx.applied ? "active" : "being activated"}.`);
        try { await reloadUserData(); } catch { /* silent */ }
        load();
      } else if (result.status === "paid" || tx?.status === "CREATED") {
        showSuccess("We're confirming your payment with the bank. Your plan starts automatically once it's confirmed.");
      } else if (result.status === "failed") {
        showError(`The payment didn't go through: ${result.reason} You can try again.`);
      }
    } catch (err) {
      showError(err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || "Couldn't start the payment. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  if (!items.length) return null;
  return items.map((item) => (
    <div key={item.requestId} role="status"
      style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, margin: "0 0 16px", padding: "14px 18px",
        borderRadius: 14, background: "linear-gradient(135deg, #1f7a3d, #2e9a52)", color: "#fff",
        boxShadow: "0 8px 22px rgba(31, 122, 61, 0.25)" }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.18)", display: "grid", placeItems: "center", flex: "none" }}>
        <i className="fas fa-crown" aria-hidden="true" />
      </div>
      <div style={{ flex: "1 1 260px", minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Your registration is complete. Start your {item.planName} plan</div>
        <div style={{ fontSize: 13, opacity: 0.92 }}>
          {item.months} months for {rupees(item.amounts?.total)}{item.amounts?.showGst && Number(item.amounts?.gst) > 0 ? " (incl. GST)" : ""}. It starts as soon as your payment is confirmed.
        </div>
      </div>
      <button type="button" onClick={() => pay(item)} disabled={!!busy}
        style={{ border: 0, borderRadius: 10, padding: "10px 18px", background: "#fff", color: "#1f7a3d", fontWeight: 700, fontSize: 14,
          cursor: busy ? "wait" : "pointer", whiteSpace: "nowrap" }}>
        {busy === item.requestId ? <><span className="spinner-border spinner-border-sm me-2" />Opening payment…</>
          : <><i className="fas fa-lock me-2" aria-hidden="true" />Pay {rupees(item.amounts?.total)}</>}
      </button>
    </div>
  ));
}
